import BaseListPage from "../../../../../base/BaseListPage";
import withRouter from "../../../../../utils/withRouter";
import {
  aggregateUseCase,
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  upsertUseCase,
} from "../../../../../usecases/object";
import PageContainer from "../../../../../components/PageContainer/PageContainer";
import Table from "../../../../../components/Table";
import * as XLSX from "xlsx-js-style";
import BillingListPresenter from "./BillingListPresenter";
import createPromise from "../../../../../utils/createPromise";
import dialog from "../../../../../components/Modal/dialog";
import ChooseCreditActionModal from "./components/ChooseCreditActionModal";
import DateRange from "../../../../../components/DateRange";

class BillingListPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new BillingListPresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      upsertUseCase(),
      deleteObjectUseCase(),
      aggregateUseCase(),
    );
    this.state = {
      objects: [],
      credit: {},
      selected: [],
      loading: false,
      total: 0,
      count: 0,
      progress: 0,
      dateFilter: {},
      showSummary: false,
      excludedClients: [],
      searchQuery: "",
      exporting: false,
    };

    this.fieldsToDisplay = ["name", "credit"];
  }

  componentDidMount() {
    this.presenter.componentDidMount();
  }

  getCollectionName() {
    return "affiliates";
  }

  onClickDeleteItem(index) {
    this.presenter.onClickDeleteItem(index);
  }

  onClickViewCreditModal(index) {
    this.presenter.onClickViewCreditModal(index);
  }

  onCreditChange(value, field) {
    const object = this.state.credit;
    object[field] = value;
    this.setCredit(object);
  }

  setCredit(credit) {
    this.setState({ credit });
  }

  onClickViewSearchLogs(index) {
    const objectID = this.state.objects[index]?.code;
    this.navigateTo(`search-logs/${objectID}`);
  }

  getActions() {
    return [
      {
        label: "View",
        onClick: this.onClickViewSearchLogs.bind(this),
        icon: "bi bi-eye-fill",
      },
    ];
  }

  formatFields(fields) {
    console.log("ff", fields);
    const { firstName, middleName, lastName, ...rest } = fields;

    return {
      ...rest,
      name: { type: "String", label: "Client Name" },
      credit: { type: "String", label: "Credit" },
      multiply: { type: "String", label: " " },
      rates: { type: "String", label: "Rate" },
      php: { type: "Number", label: "Php" },
    };
  }

  // formatObjects(objects) {
  //   return objects.map((object) => {
  //     const { ...rest } = object || {};

  //     return {
  //       ...rest,
  //       credit: object.credit || 0,
  //       rate: object?.rate,
  //       php: object?.php,
  //     };
  //   });
  // }
  formatObjects(objects) {
    return objects.map((object) => {
      const { ...rest } = object || {};

      return {
        ...rest,
        credit: object.credit || 0,
        multiply: "x",
        rate: object?.rate,
        php: `₱${Number(object?.php || 0).toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      };
    });
  }

  setDateFilter(date) {
    this.setState({ dateFilter: date });
  }

  setLoading(value) {
    this.setState({ loading: value });
  }

  setProgress(completed, total) {
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    this.setState({ progress: percent });
  }

  setExporting(value) {
    this.setState({ exporting: value });
  }

  onExportReady(data) {
    if (!data || data.length === 0) {
      this.showError("No data to export for the selected period.");
      return;
    }

    const headers = ["Searched Name", "Searcher", "Date", "Client", "Branch"];

    const toRow = (item) => {
      const hasName =
        item.searched_first_name ||
        item.searched_middle_name ||
        item.searched_last_name ||
        item.searched_alias;
      return [
        hasName
          ? `${item.searched_first_name || ""} ${item.searched_middle_name || ""} ${item.searched_last_name || ""} ${item.searched_alias || ""}`.trim()
          : item.searched_company || "",
        `${item.searcher?.firstName || ""} ${item.searcher?.middleName || ""} ${item.searcher?.lastName || ""}`.trim(),
        new Date(item.createdAt).toLocaleString("en-US"),
        item.searcher?.affiliate?.code || item.searcher?.affiliate?._id || "",
        item.searcher?.branch?.code || item.searcher?.branch?._id || "",
      ];
    };

    // Group by client name/id
    const groups = new Map();
    for (const item of data) {
      const clientKey =
        item.searcher?.affiliate?.name ||
        item.searcher?.affiliate?._id ||
        "Unknown";
      if (!groups.has(clientKey)) groups.set(clientKey, []);
      groups.get(clientKey).push(item);
    }

    const wb = XLSX.utils.book_new();

    for (const [clientName, items] of groups) {
      const wsData = [headers, [], ...items.map(toRow)];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Bold header row
      headers.forEach((_, idx) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            font: { bold: true },
            alignment: { horizontal: "center" },
          };
        }
      });

      // Sheet names must be ≤31 chars and no special chars
      const sheetName = clientName.replace(/[\\/*?[\]:]/g, "").slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName || "Unknown");
    }

    XLSX.writeFile(wb, "billing_export.xlsx");
  }

  exportCSV() {
    const { objects, searchQuery } = this.state;

    const q = searchQuery.trim().toLowerCase();
    const rows = (
      q
        ? objects.filter(
            (o) =>
              (o.name || "").toLowerCase().includes(q) ||
              (o.code || "").toLowerCase().includes(q),
          )
        : objects
    ).map((o) => [
      o.name || o.code || "",
      o.credit || 0,
      o.rate || 0,
      Number(o.php || 0).toFixed(2),
    ]);

    const csv = [["Client Name", "Credit", "Rate", "Php"], ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "billing.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  render() {
    const schema = this.getSchema(this.getCollectionName());
    const {
      objects,
      loading,
      count,
      dateFilter,
      excludedClients,
      searchQuery,
    } = this.state;

    const hasDateFilter = dateFilter?.gte && dateFilter?.lte;

    const q = searchQuery.trim().toLowerCase();
    const filteredObjects = q
      ? objects.filter(
          (o) =>
            (o.name || "").toLowerCase().includes(q) ||
            (o.code || "").toLowerCase().includes(q),
        )
      : objects;

    const activeObjects = hasDateFilter
      ? filteredObjects.filter(
          (o) =>
            (o.credit || 0) > 0 && !excludedClients.includes(o.code || o.id),
        )
      : [];

    const totalPhp = activeObjects.reduce(
      (sum, o) => sum + (Number(o.php) || 0),
      0,
    );

    const removeClient = (key) =>
      this.setState((prev) => ({
        excludedClients: [...prev.excludedClients, key],
      }));

    const formatDate = (iso) => {
      if (!iso) return "";
      return new Date(iso).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    };

    return (
      <PageContainer
        title="Billings"
        infiniteScroll={{
          loadMore: this.loadMore.bind(this),
          hasMore: !loading && count > objects.length,
        }}
      >
        <div className="d-flex flex-row align-items-center gap-3">
          <DateRange
            field="createdAt"
            onChange={this.onChangeDate.bind(this)}
          />
          <button
            className="btn btn-outline-success btn-sm"
            disabled={this.state.exporting}
            onClick={() => this.presenter.exportExcel()}
          >
            {this.state.exporting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                />
                Exporting...
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-excel me-1" />
                Export Excel
              </>
            )}
          </button>
          {/* <Search
            schemas={this.getSchemas()}
            onSubmit={this.searchSubmit.bind(this)}
            fields={schema.fields}
          /> */}
          <div className="input-group input-group-sm" style={{ width: 220 }}>
            <span className="input-group-text bg-white border-end-0">
              <i
                className="bi bi-search text-muted"
                style={{ fontSize: ".8rem" }}
              />
            </span>
            <input
              type="text"
              className="form-control border-start-0 ps-0"
              placeholder="Search client..."
              value={this.state.searchQuery}
              onChange={(e) => this.setState({ searchQuery: e.target.value })}
            />
          </div>
          {hasDateFilter && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => this.setState({ showSummary: true })}
            >
              <i className="bi bi-receipt me-1" />
              Billing Summary
            </button>
          )}
        </div>
        {loading && (
          <div className="my-2 w-100">
            <div className="progress" style={{ height: "6px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                role="progressbar"
                style={{ width: `${this.state.progress}%` }}
              />
            </div>
            <small className="text-muted">
              Loading credits... {this.state.progress}%
            </small>
          </div>
        )}

        {this.state.showSummary && (
          <div
            className="modal d-block"
            style={{ backgroundColor: "rgba(0,0,0,.5)" }}
            onClick={() => this.setState({ showSummary: false })}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
              style={{ maxWidth: 460 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                {/* Header */}
                <div
                  className="modal-header bg-primary border-0 px-4 pt-4 pb-2"
                  // style={{
                  //   background:
                  //     "linear-gradient(135deg, #1f1c58 0%, #3b37a0 100%)",
                  // }}
                >
                  <div>
                    <h5 className="modal-title fw-bold text-white mb-0">
                      Billing Summary
                    </h5>
                    <p
                      className="text-white-50 mb-0 mt-1"
                      style={{ fontSize: ".78rem" }}
                    >
                      {formatDate(dateFilter.gte)} –{" "}
                      {formatDate(dateFilter.lte)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white opacity-75"
                    onClick={() => this.setState({ showSummary: false })}
                  />
                </div>

                {/* Scrollable body */}
                <div
                  className="modal-body p-0"
                  style={{ maxHeight: 340, overflowY: "auto" }}
                >
                  <table className="table table-hover table-sm mb-0 align-middle">
                    <thead
                      className="sticky-top bg-white"
                      style={{
                        fontSize: ".72rem",
                        boxShadow: "0 1px 0 #dee2e6",
                      }}
                    >
                      <tr className="text-uppercase text-secondary">
                        <th className="ps-4 py-2 fw-semibold">#</th>
                        <th className="py-2 fw-semibold">Client</th>
                        <th className="text-end py-2 fw-semibold">Amount</th>
                        <th className="pe-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {activeObjects.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-muted py-4"
                          >
                            No data for selected period
                          </td>
                        </tr>
                      ) : (
                        activeObjects.map((o, i) => (
                          <tr key={o.code || o.id}>
                            <td
                              className="ps-4 py-2 text-secondary"
                              style={{ fontSize: ".78rem" }}
                            >
                              {i + 1}
                            </td>
                            <td
                              className="py-2 fw-medium"
                              style={{ fontSize: ".85rem" }}
                            >
                              {o.name || o.code}
                            </td>
                            <td
                              className="text-end py-2"
                              style={{ fontSize: ".85rem" }}
                            >
                              ₱
                              {Number(o.php || 0).toLocaleString("en-PH", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td className="pe-3 py-2 text-end">
                              <button
                                type="button"
                                className="btn btn-sm p-0 text-danger"
                                style={{
                                  border: "none",
                                  background: "none",
                                  lineHeight: 1,
                                }}
                                onClick={() => removeClient(o.code || o.id)}
                              >
                                <i
                                  className="bi bi-trash"
                                  style={{ fontSize: ".85rem" }}
                                />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer total */}
                <div
                  className="px-4 py-3 d-flex justify-content-between align-items-center"
                  style={{
                    borderTop: "2px solid #dee2e6",
                    background: "#f8f9fa",
                  }}
                >
                  <span
                    className="text-secondary fw-medium"
                    style={{ fontSize: ".82rem" }}
                  >
                    {activeObjects.length} client
                    {activeObjects.length !== 1 ? "s" : ""}
                  </span>
                  <div className="text-end">
                    <div className="text-muted" style={{ fontSize: ".72rem" }}>
                      Total
                    </div>
                    <div
                      className="fw-bold"
                      style={{ fontSize: "1.05rem", color: "#1f1c58" }}
                    >
                      ₱
                      {totalPhp.toLocaleString("en-PH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <Table
          fields={this.formatFields(schema.fields)}
          objects={this.formatObjects(filteredObjects)}
          collapsable
          progress={loading}
          actions={this.getActions.bind(this)}
          excludeFields={Object.keys(schema.fields).filter(
            (key) => !this.fieldsToDisplay.includes(key),
          )}
        />
      </PageContainer>
    );
  }
}

export default withRouter(BillingListPage);
